import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { useDropdownMenu } from '@utils/assessmentReportUtils'
import { appSessionStorage } from '@utils/storageUtils'
import { Flex } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { Database_Procs_TeamBonusGames_Item } from '@suntex.fim/api-client'
import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import {
  ExportMenu,
  LevelSelect,
  LoaderOverlay,
  SearchInput
} from '@suntex.fim/ui'

import columns, {
  getBonusGamesColumnKeys,
  hidableSelectLabel
} from './AssessmentReport/AssessmentReportBonusTable'
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

export const bonusLevelOptions = [
  {
    key: 0,
    label: 'Bonus Games 1-2'
  },
  {
    key: 1,
    label: 'Bonus Games 3-4'
  },
  {
    key: 2,
    label: 'Bonus Games 5-6'
  },
  {
    key: 3,
    label: 'Bonus Games 7-8'
  }
]

/**
 * returns JSX for gym report
 */
function AssessmentReportBonus() {
  const { api } = useFramework()
  const { bonusLevel } = useAppQueryString()
  const team = useOptionalTeamInfo()
  const selectedLevel = {
    level: appSessionStorage.bonusLevel || Number(bonusLevel)
  }
  const [filters, setFilters] = useState(selectedLevel)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  const [isExporting, setIsExporting] = useState(false)

  const { data: assessmentReport = [], isLoading } = useQuery({
    queryKey: ['assessment', 'bonusTeam', team?.id],
    queryFn: async () => {
      const response = await api.get_Assessment_BonusTeam({
        teamId: team!.id
      })
      return response.teamBonusGames || []
    },
    enabled: team?.id != null
  })
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }

  const memoColumns = useMemo<ColumnType<Database_Procs_TeamBonusGames_Item>[]>(
    () => columns({ level: filters.level, graphColor: '#3FB2FF', sendMessage }),
    [filters.level]
  )
  const subHeaders = getBonusGamesColumnKeys(filters.level)
  const linkOptions = [
    ...subHeaders.subHeaders[0],
    ...subHeaders.subHeaders[1]
  ].map((label) => ({
    key: `word-problems/${filters.level}/${label}`,
    label: hidableSelectLabel[label]
  }))

  const { table, Table } =
    useAssessmentTable<Database_Procs_TeamBonusGames_Item>({
      data: assessmentReport,
      columns: memoColumns,
      isLoading,
      globalFilter: searchQuery,
      initialSorting: [{ id: 'displayName', desc: false }],
      horizontalScroll: true
    })
  const selectedRow = table.getSelectedRowModel().flatRows

  // Update localStorage whenever level changes
  useEffect(() => {
    appSessionStorage.bonusLevel = filters.level
  }, [filters.level])
  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = useMemo(
    () =>
      sendMessageUsers.map(
        ({ original }: { original: Database_Procs_TeamBonusGames_Item }) => ({
          label: original.displayName || original.userId,
          value: original.playerId
        })
      ),
    [sendMessageUsers]
  )

  const items = useDropdownMenu({
    table,
    hidableSelectLabel,
    fileName: `${team?.name}_bonus`,
    hasColumnChange: [
      'Bonus 1',
      'Bonus 2',
      'Bonus 3',
      'Bonus 4',
      'Bonus 5',
      'Bonus 6',
      'Bonus 7',
      'Bonus 8'
    ],
    transformCols: (id: string, colValue: any) => {
      if (!colValue) {
        return {}
      }
      return Object.entries(colValue).reduce((newVale, [key, value]) => {
        return {
          ...newVale,
          [`${hidableSelectLabel[id][0]}${key}`]: value || 0
        }
      }, {})
    },
    setIsLoading: setIsExporting
  })
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay isLoading={isLoading || isExporting} />
        <AssessmentReportNavigation
          exportButton={
            <ExportMenu
              ariaLabel="Export report options"
              items={items}
              trigger={['click']}
            />
          }
          filters={
            <Flex gap={14}>
              <Flex gap={6} flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Level</p>
                <LevelSelect
                  value={filters.level}
                  onChange={(level) => {
                    setFilters((prev) => ({ ...prev, level }))
                    setSearchQuery('')
                  }}
                  options={bonusLevelOptions}
                />
              </Flex>
            </Flex>
          }
        />
        <Flex className="tiles-container" wrap="wrap">
          <Flex className="left" vertical gap="20px" flex="1 1 560px">
            <AssessmentReportSummaryDashboard />
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">Team Players</h4>

            <Flex gap="10px" justify="space-between" wrap>
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              <Button
                intent="tertiary"
                size="small"
                disabled={isDisabled}
                onClick={() => setIsModalOpen(true)}
                className="print-hidden"
              >
                SEND MESSAGE
              </Button>
            </Flex>
          </Flex>
          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          level={bonusLevelOptions[filters.level]?.label}
          linkOptions={linkOptions}
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
          reportType="Bonus"
        />
      </Flex>
    </>
  )
}

export default AssessmentReportBonus
